import Bubble from '@/legacy-src/components/Bubble'
import type { ExtendedDiagnosisFields } from '@/legacy-src/components/diagnosis/client/types'
import Icon from '@/legacy-src/components/icons/Icon'
import useSpecializations from '@/legacy-src/hooks/useSpecializations'
import type { TherapistType } from '@/legacy-src/types/TherapistStateType'
import { getTranslate } from '@/legacy-src/utilities/i18n'

import { Heading } from '@/src/domains/design-system/components/Heading'
import { Tag } from '@/src/domains/design-system/components/Tag'
import { Text } from '@/src/domains/design-system/components/Text'

import { TherapistsPreviewInRegistrationTitle } from './TherapistsPreviewInRegistrationTitle'

export interface TherapistsPreviewInRegistrationVersionAProps {
  therapist: TherapistType
  nameVocative?: string
  diagnosis: ExtendedDiagnosisFields
}

const MAX_VISIBILE_SPECIALIZATIONS = 4
const MIN_RECOMMENDATIONS = 5

export const TherapistsPreviewInRegistrationVersionA = ({
  therapist,
  nameVocative,
  diagnosis,
}: TherapistsPreviewInRegistrationVersionAProps) => {
  const { t } = getTranslate()

  const { specializations } = useSpecializations()

  const price = therapist.prices?.find((price) => price.serviceId === (diagnosis.service[0] ?? diagnosis.service))

  const therapistSpecializations = [
    ...diagnosis.specialization,
    ...therapist.majorSpecializations,
    ...(therapist?.minorSpecializations ?? []),
  ]
    .map((specializationId) => specializations[specializationId.toString()])
    .filter((specialization, index, array) => array.indexOf(specialization) === index)

  const visibleSpecializations = therapistSpecializations.slice(0, MAX_VISIBILE_SPECIALIZATIONS)
  const restSpecializationsCount = therapistSpecializations.length - MAX_VISIBILE_SPECIALIZATIONS

  return (
    <>
      <div className="mb-10">
        <TherapistsPreviewInRegistrationTitle nameVocative={nameVocative} sex={therapist.sex} />
      </div>
      <div className="mb-10 flex w-full flex-col items-start gap-10 rounded-3xl bg-blue-500 p-5 tablet:flex-row tablet:items-center tablet:gap-5">
        <div className="flex flex-1 items-center gap-3">
          <Bubble
            imgUrl={therapist.image ?? '/images/svg/misc/therapist-placeholder.svg'}
            sizeClass="w-20 h-20"
            className="inline-block shrink-0"
          />
          <div className="flex flex-1 flex-col gap-2">
            <Heading element="h2" variant="xl" className="m-0">
              {therapist.firstName} {therapist.lastName}
            </Heading>
            {therapist.recommendations >= MIN_RECOMMENDATIONS ? (
              <p className="m-0 flex items-center gap-1">
                <Icon icon="heart-rebrand" className="mr-1" />
                <Text variant="sm" element="span" weight="bold">
                  {t('therapistProfile.recommendations', { recommendations: therapist.recommendations })}
                </Text>
              </p>
            ) : null}
            <p className="m-0 flex items-center gap-1">
              <Icon icon="appointment" className="mr-1" />
              <Text variant="sm" element="span" weight="bold">
                {t('therapistProfile.price', { price: price?.price })}
              </Text>
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-wrap items-start gap-2">
          <Text element="span">
            {t('diagnosisTest.register.versionA.therapist.name', {
              name: therapist.firstName,
            })}
          </Text>
          <Tag size="xs" variant="yellow">
            {t('diagnosisTest.register.therapist.experience', {
              experience: therapist.experience,
            })}
          </Tag>
          <Text element="span">{t('diagnosisTest.register.versionA.therapist.topics')}</Text>
          <>
            {visibleSpecializations.map((specialization) => {
              if (specialization) {
                return (
                  <Tag size="xs" key={specialization} variant="sand">
                    {specialization}
                  </Tag>
                )
              }
              return null
            })}
            {restSpecializationsCount > 0 ? (
              <Tag size="xs" variant="sand">
                +{restSpecializationsCount}
              </Tag>
            ) : null}
          </>
        </div>
      </div>
    </>
  )
}
